'use client'

/**
 * The shared `<Select>` — an ADAPTER over `@sai/ui`'s `Select`
 * (#13213, Stage 4 slice 11 of #13197).
 *
 * `@/components/ui/select` stays the permanent import path and its PROP
 * SIGNATURE IS FROZEN: the 18 consumer files did not change by one line. Every
 * mismatch between what those callers write and what sai-ui (Radix
 * `@radix-ui/react-select`) expects is absorbed HERE, and each absorption has a
 * case in `tests/unit/components/ui/select-sai-adapter-13213.test.tsx`. The
 * pre-existing pins in `tests/unit/components/ui/select.test.tsx` (#7918,
 * #12792, #12796, #12865, #13186) pass UNMODIFIED — that suite is the contract
 * this file had to meet, not a suite to re-baseline.
 *
 * SIX TRANSLATIONS, EACH LOAD-BEARING
 *
 * 1. THE EMPTY-STRING ITEM VALUE. Radix reserves `''` as the Select's
 *    "no selection" sentinel and THROWS when an item claims it. A live call
 *    site passes exactly that —
 *    `src/app/admin/dev-priorities-entry/dev-priorities-entry-form.tsx:436`.
 *    So `''` is mapped to {@link EMPTY_VALUE_SENTINEL} on
 *    the way IN (item values, the root's `value`/`defaultValue`) and back to
 *    `''` on the way OUT (`onValueChange`). The mapping is a bijection on one
 *    value, applied at every boundary, so it cannot lose information: a caller
 *    that passes `''` gets `''` back, and an item whose value is `''` is
 *    selected exactly when the caller's value is `''`. It is NOT a guess about
 *    which of the two meanings `''` carries — "the None option is chosen" and
 *    "nothing is chosen" stay the same single value on the Cadence side of the
 *    boundary, which is what keeps them indistinguishable in the direction the
 *    callers already rely on.
 *
 * 2. THE TRIGGER'S ARIA CONTRACT. Radix's trigger advertises no
 *    `aria-haspopup` and carries `aria-controls` unconditionally — including
 *    while closed, when the panel it names is unmounted (a dangling IDREF,
 *    `aria-valid-attr-value`). Cadence pins both the other way (#12796,
 *    #12865). Both are passed explicitly below: sai-ui spreads its `rest`
 *    props onto `RadSelect.Trigger`, and Radix spreads the consumer's props
 *    AFTER its own attributes, so an explicit value wins and an explicit
 *    `undefined` REMOVES the attribute. Same mechanism supplies the panel's id
 *    ({@link SelectContextValue.listboxId}), so `aria-controls` resolves to
 *    the listbox this trigger opened rather than to a Radix-generated id this
 *    file cannot see.
 *
 * 3. THE TRIGGER'S ACCESSIBLE NAME. sai-ui types its trigger with
 *    `AccessibleFormFieldProps`, a discriminated union that admits at most ONE
 *    of `ariaLabel` / `ariaLabelledBy`. Cadence callers may pass BOTH, and
 *    #13186 turns on them being carried together (see {@link
 *    SelectTriggerNaming}). The hyphenated `aria-label` / `aria-labelledby`
 *    are therefore forwarded through sai-ui's `...rest` instead of through its
 *    camelCase props: `transformAccessibilityProps` only strips the camelCase
 *    spellings, so the hyphenated pair rides through untouched and both land
 *    on the element.
 *
 * 4. THE OPTION'S TAB STOP. Radix runs its options as a focus-scoped
 *    composite: every option is `tabIndex={-1}` and the CONTENT holds focus.
 *    #12792 pins an enabled option at `0` and a disabled one at `-1`, because
 *    Cadence's options are reachable by Tab. `tabIndex` is passed per item;
 *    Radix's own `data-highlighted` roving is unaffected, and its Enter/Space
 *    activation still runs.
 *
 * 5. THE PANEL'S NAME. Radix names nothing on its content. The naming
 *    machinery below (trigger registers, panel copies) is unchanged from the
 *    bespoke component and its output is spread onto sai-ui's `SelectContent`,
 *    which forwards it to the element carrying `role="listbox"`. Read
 *    {@link SelectTriggerNaming} before touching any of it — the rule is
 *    subtle and was measured in a real browser, not reasoned about.
 *
 * 6. THE PORTAL PROP, AND CLOSE-ON-SCROLL. Radix ALWAYS portals its content to
 *    `document.body` and positions it with floating-ui, so `portal` no longer
 *    selects a render path — the clipping problem it was added for is gone in
 *    both modes. It is kept in the signature (11 call sites pass it) because
 *    the signature is frozen, and it is now EFFECTIVELY MARKER-ONLY: the one
 *    thing it still selects that anybody can observe is the
 *    `data-select-portal` attribute.
 *
 *    In particular it no longer selects close-on-ancestor-scroll, and the two
 *    pins that appear to keep that alive (`select.test.tsx:126-180`) are not
 *    evidence of it. Radix wraps the open content in `RemoveScroll`
 *    (`@radix-ui/react-select` `dist/index.mjs`, `SelectContentImpl`), which
 *    locks the document and blocks wheel/touch outside the panel while it is
 *    open — so in a real browser a user cannot produce the ancestor scroll the
 *    listener waits for. Those pins dispatch a synthetic `new Event('scroll')`
 *    in jsdom, which `RemoveScroll` has no way to intercept, so they pass
 *    without exercising the behaviour. The listener is retained because it is
 *    harmless and the signature is frozen; do not go looking for its effect.
 *
 * WHAT IS DELIBERATELY NOT ABSORBED — read this before assuming a test is
 * broken. Three Radix behaviours are passed through rather than fought,
 * because reproducing the old ones means running a SECOND focus manager
 * alongside Radix's, which is a worse component than either model on its own.
 *
 * a. THE OPEN PANEL IS A COMPOSITE, NAVIGATED BY ARROW KEYS, NOT BY TAB.
 *    The bespoke component made every enabled option a native tab stop, so a
 *    keyboard user walked the options with Tab. Radix implements the WAI-ARIA
 *    listbox pattern instead: it traps focus in the panel (`trapped:
 *    context.open`), `preventDefault()`s Tab outright
 *    (`@radix-ui/react-select` `dist/index.mjs:478`) and moves the selection
 *    with Up/Down/Home/End plus typeahead, returning focus to the trigger when
 *    the panel closes. Keyboard-only operation is preserved and gains
 *    typeahead and Home/End; the KEY that traverses changed. `tabIndex` is
 *    still passed per option because #12792 pins it and because it is what
 *    keeps a disabled option out of any tab order — but Tab no longer moves
 *    between options, so treat the attribute as the pin it is rather than as
 *    a traversal mechanism. Two tests written against the old traversal were
 *    updated with this migration and say so at the assertion:
 *    `tests/unit/components/actionboard/board.test.tsx`. (The second one,
 *    `golden-path-actionboard-week-range-13088.spec.ts`, was deleted with the
 *    week-range control in #13240 — named here so a reader does not go looking
 *    for it.)
 *
 * b. THE REST OF THE DOCUMENT IS ARIA-HIDDEN WHILE THE PANEL IS OPEN
 *    (`hideOthers`), which is correct for a modal listbox. A test that queries
 *    `getByRole('combobox')` WHILE a select is open now matches zero elements;
 *    capture the trigger before opening it, which every pinned case already
 *    does.
 *
 * c. `onValueChange` FIRES ON A CHANGE, NOT ON EVERY PICK. Re-choosing the
 *    option already selected notifies nobody, where the bespoke component
 *    called back every time. All 18 consumers assign the value to state, so a
 *    no-op assignment is what they lose; a caller wanting "the user confirmed
 *    the current value" was never able to express it here anyway.
 *
 * d. A SELECT INSIDE A `<form>` ALSO RENDERS A HIDDEN NATIVE `<select>`
 *    mirroring its options (Radix's `BubbleSelect`, gated on
 *    `trigger.closest('form')` with no opt-out). It is `aria-hidden` and
 *    visually hidden, so no user or screen reader sees it — but its
 *    `<option>` text IS in the DOM, so a bare `getByText('…')` that used to
 *    match the trigger's label now matches twice. Scope such a query to the
 *    trigger.
 *
 * e. `aria-selected` IS NOW "SELECTED AND FOCUSED", NOT "SELECTED". Radix
 *    renders `aria-selected={isSelected && isFocused}` on `role="option"`,
 *    where the bespoke component rendered `aria-selected={isSelected}`. So the
 *    chosen option reports `aria-selected="false"` whenever it is not the
 *    focused one. This is Radix's deliberate reading of the listbox pattern
 *    and axe does not flag it, but it is a real query change: assert
 *    `data-state="checked"` for "is this the chosen option", which is what the
 *    adapter suite does.
 *
 * f. THE TRIGGER'S CHEVRON IS ASYNC AND CLIENT-ONLY. sai-ui's `Icon`
 *    dynamic-imports its path data and returns `null` while loading, so the
 *    trigger renders chevron-less on the server and until the chunk resolves.
 *    `@/components/icons`' `ChevronDown` was synchronous. Shared with the
 *    `button.tsx` adapter (#13204); recorded because it is a visible
 *    first-paint change at every call site.
 *
 * VISUAL DELTAS INHERITED FROM SAI-UI'S BASE CLASSES. None of these is
 * absorbed, because each is either sai-ui's house style winning on purpose or
 * cheaper to live with than to fight:
 *
 *   - The selected-option check MOVED FROM THE LEFT EDGE TO THE RIGHT and is
 *     now stroked `currentColor` rather than `text-status-blue-text`. It is
 *     Radix's `ItemIndicator` in sai-ui's `end`-aligned span, and the item's
 *     padding flipped with it (`pl-8 pr-2` -> `ps-2 pe-8`).
 *   - TRIGGER LABEL TEXT IS `font-medium` AND CLAMPED TO ONE LINE.
 *     sai-ui's trigger base carries `font-medium` and `[&>span]:line-clamp-1`,
 *     and `SelectValue` renders a `<span>` that is a direct child, so a long
 *     value truncates where it previously wrapped.
 *   - `focus:ring-offset-2` is added to the trigger by sai-ui's base;
 *     tailwind-merge does not displace it.
 *   - THE PANEL IS NO LONGER EXACTLY THE TRIGGER'S WIDTH. It was `w-full`
 *     (inline) / `coords.width` (portal); it is now
 *     `min-w-(--radix-select-trigger-width)` and content-sized, so it can grow
 *     wider than its trigger. Call sites carrying an explicit `min-w-*` on
 *     `SelectContent` were compensating for the old behaviour and now stack
 *     with this one.
 *
 * SAI-UI MARKS ITS `Select` `@deprecated` ("Use Combobox for single-select
 * controls instead", `dist/components/select.js`), AND THIS SLICE TARGETS IT
 * ANYWAY. `Combobox` is a different interaction model — a filterable text
 * input — and #13213's binding constraint is a frozen prop signature across 18
 * call sites that pass no filter, no `onInputChange` and no async loader.
 * Adopting it here would push a prop-shape change onto every consumer, which
 * is the one thing the slice forbids. Recorded rather than resolved: the
 * convergence target for this primitive is a `Combobox` migration that owns
 * its consumers, and it is a different unit of work from an adapter swap.
 *
 * NO THEME AWARENESS, DELIBERATELY. The package's components reference token
 * NAMES, and `src/tokens/` decides what they resolve to in light and dark —
 * so there is nothing here to branch on. Wanting to branch from this file
 * means a token is missing in `src/tokens/`, which is a fix for that layer.
 */

import {
  Select as SaiSelect,
  SelectContent as SaiSelectContent,
  SelectItem as SaiSelectItem,
  SelectTrigger as SaiSelectTrigger,
} from '@sai/ui/components'
import {
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { cn } from '@/lib/utils'

// ============================================================================
// The empty-string value, and the one place it is translated
// ============================================================================

/**
 * What `''` becomes on the Radix side of the boundary.
 *
 * Radix throws `Select.Item` "must have a value prop that is not an empty
 * string" — `''` is its own "nothing is selected" sentinel and an item may not
 * claim it. Three Cadence call sites do, and the slice forbids touching
 * consumers, so the value is renamed at the boundary and renamed back on the
 * way out.
 *
 * The string is deliberately one no caller could pass by accident. It never
 * escapes this module: `toRadixValue` is applied at every prop that reaches
 * Radix and `fromRadixValue` at every value that comes back, so a consumer
 * cannot observe it in `onValueChange`, in `SelectValue`, or in the `value` it
 * passed in.
 */
const EMPTY_VALUE_SENTINEL = '__cadence_select_empty_value__'

const toRadixValue = (value: string) =>
  value === '' ? EMPTY_VALUE_SENTINEL : value

const fromRadixValue = (value: string) =>
  value === EMPTY_VALUE_SENTINEL ? '' : value

// ============================================================================
// Select Context
// ============================================================================

/**
 * How a `SelectTrigger` is named — the id it rendered, and its own
 * `aria-label` if it has one.
 *
 * ## Why the panel copies the label instead of referencing the trigger
 *
 * `SelectContent`'s `role="listbox"` panel must be named, or #12796's role fix
 * trades `aria-required-parent` for `aria-input-field-name` (serious, wcag2a).
 * It was named with `aria-labelledby={triggerId}`, and that reference resolves
 * — but it does not produce a name.
 *
 * The trigger is `role="combobox"`, which accname step 2E calls an embedded
 * control. Recursing into one makes step 2C IGNORE its `aria-label` and take
 * the control's VALUE instead, which for a combobox is the chosen option's
 * text — and the chosen option is inside the panel being named, so the
 * computation returns EMPTY. Measured on `/actionboard` with the #13088
 * week-range control open: Chromium reported the listbox with no name at all,
 * which is what kept that control's golden path red (#13186). The measurement
 * stands and the carve-out is still live; the control and its spec were
 * removed in #13240, so there is no longer a file to cite for it. jsdom's `dom-accessibility-api` does not implement that carve-out
 * and resolves the same markup to the trigger's label, which is why no unit
 * test saw it.
 *
 * So the panel copies whichever naming attributes the CALLER put on the
 * trigger, verbatim and together:
 *
 * - `aria-label` — copied as a STRING. No recursion happens, so no carve-out
 *   applies.
 * - `aria-labelledby` — copied as the same IDREF. This is safe where `{id}`
 *   is not: it points at whatever the caller named the trigger with (a
 *   `<label>`, a heading, a `<span>`), and none of those is an embedded
 *   control, so recursing into one yields real text.
 *
 * **Both, when the caller passed both**, rather than picking one. The panel
 * then resolves them by the same accname steps in the same order as the
 * trigger, so the two names are equal by construction — including when the
 * caller's `aria-labelledby` dangles and step 2C's `aria-label` is what
 * actually names the trigger.
 *
 * When the caller passed NEITHER, the panel falls back to
 * `aria-labelledby={id}`. That fallback is never paired with an `aria-label`,
 * because it is guaranteed to resolve — the trigger is in the DOM — so it
 * would win over one every time.
 *
 * ## The no-attributes case is a KNOWN RESIDUAL, not a name
 *
 * Be exact about it, because the shape is the one this whole comment exists to
 * warn about. A trigger named only by its own content — the common
 * `<label htmlFor={triggerId}>` spelling passes neither ARIA attribute — has no
 * string and no foreign IDREF to copy, so case 3 points back at the combobox
 * and **the panel is still unnamed in Chromium**, exactly as analysed above.
 * It is retained because it cannot dangle and is what shipped before, not
 * because it names anything.
 *
 * That residual is live on roughly half the `<SelectTrigger>` call sites in
 * `src/` and is tracked separately — closing it means either giving those
 * triggers an `aria-label` or resolving `HTMLButtonElement.labels` here, and
 * both change accessible names on surfaces this component does not own.
 *
 * **axe will not find it for you.** axe-core implements accname without the 2E
 * carve-out, so it resolves the reference and reports the panel as named. Only
 * a real-browser assertion on `getByRole('listbox', { name })` can see it.
 */
interface SelectTriggerNaming {
  /** The id actually rendered on the trigger. */
  id: string
  /** The trigger's own `aria-label`, when the caller passed one. */
  label?: string
  /**
   * The trigger's own `aria-labelledby`, when the caller passed one. Copied
   * onto the panel in preference to `id`: the caller's IDREF points at
   * something that is not a combobox, and `id` points at one.
   */
  labelledBy?: string
}

interface SelectContextValue {
  value: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * The panel element Radix mounted, held so the close-on-scroll listener can
   * tell the panel's OWN scroll from an ancestor's. Wheeling the option list
   * must not dismiss the panel being wheeled.
   */
  panelRef: RefObject<HTMLDivElement | null>
  /**
   * How the trigger is NAMED, published so `SelectContent` can name its
   * `role="listbox"` panel the SAME way rather than by pointing at the
   * trigger. See {@link SelectTriggerNaming} for why pointing does not work.
   *
   * Registered by the trigger rather than derived here, because all three
   * parts live on the trigger: callers may pass their own `id` (a visible
   * `<label htmlFor>` needs one), their own `aria-label` and their own
   * `aria-labelledby`. All three are registered together so the panel cannot
   * be named from one while the trigger is named from another — the trigger
   * itself resolves `aria-labelledby` over `aria-label`, so a panel copying
   * only the label would silently carry a different name than the control it
   * belongs to.
   *
   * An `aria-labelledby` pointing at an id that is not in the DOM is an
   * `aria-valid-attr-value` violation — a worse defect than the unnamed
   * listbox it was meant to fix — which is why the `id` half is the rendered
   * one rather than an assumed one.
   */
  triggerNaming: SelectTriggerNaming | null
  registerTriggerNaming: (naming: SelectTriggerNaming) => void
  /**
   * The id rendered on the `role="listbox"` panel, so the trigger can point
   * `aria-controls` at it WHILE OPEN.
   *
   * Minted here and pushed onto BOTH halves rather than left to Radix, which
   * generates a content id of its own and exposes it to nobody: the trigger
   * needs the value while the panel is unmounted, and this file cannot read
   * Radix's. Passing our own id to `SelectContent` overrides Radix's on the
   * element that carries `role="listbox"`, so the reference resolves to the
   * panel a user actually sees.
   *
   * ARIA 1.2 makes `aria-controls` REQUIRED on `role="combobox"`, and axe
   * reports its absence as `aria-required-attr` at CRITICAL impact. #12796
   * gave the panel its role and its name; the reference back from the control
   * that opens it was the half still missing, and it went unseen because no
   * axe-scanning route had scanned a Select in its OPEN state
   * (`tests/e2e/critical-path/golden-path-sai-actionboard-12865.spec.ts` is
   * the one that does).
   */
  listboxId: string
  /**
   * What each item's value should DISPLAY as, keyed by value.
   *
   * `SelectValue` rendered the raw `value` — so a trigger sat closed reading
   * `FREE_TEXT`, `next-date` or `7` while the open list beside it read "Free
   * text", "Next key date" and "1 week". 54 of the 57 `<SelectItem>`s with
   * literal text children in this repo have a label that differs from their
   * value, so the closed trigger was wrong nearly everywhere it was not a
   * person's name.
   *
   * Built by TRAVERSING the root's `children` rather than registered by the
   * items, and that is forced rather than preferred: the panel is unmounted
   * whenever it is closed (see `listboxId` above), so no item is rendered at
   * the only moment the trigger needs its label. An effect-based registry
   * would populate on first open and read empty before it — the trigger would
   * print the raw value until the user opened the list, which is the defect
   * with an extra step.
   */
  itemLabels: ReadonlyMap<string, ReactNode>
}

const SelectContext = createContext<SelectContextValue | null>(null)

/**
 * How deep {@link collectItemLabels} will descend looking for `SelectItem`s.
 *
 * Bounded rather than trusting the tree to be finite. React element trees are
 * acyclic, so this cannot loop — the cap is against a pathologically nested
 * consumer costing more than the lookup is worth, and 12 clears every call
 * site in this repo (the deepest is `SelectContent` > `.map()` > fragment >
 * `SelectItem`, at 4).
 */
const MAX_ITEM_SCAN_DEPTH = 12

/**
 * Walk a `Select`'s children and record what each item value displays as.
 *
 * Reads the UNRENDERED element tree — `props.children` on plain elements — so
 * it works while the panel is closed and its items are unmounted.
 *
 * Two shapes are deliberately out of reach, and both fall back to the raw
 * value rather than to nothing: items produced INSIDE another component's
 * body (that component is an element here, not its output), and items whose
 * value is computed at render time by something this traversal cannot
 * evaluate. `.map()` over an array is NOT one of those — its output is an
 * array in `props.children`, which is walked like any other child.
 *
 * First writer wins, matching Radix: with two items sharing a value, the
 * earlier one is what the list selects.
 */
function collectItemLabels(
  node: ReactNode,
  into: Map<string, ReactNode>,
  depth = 0,
): void {
  if (depth > MAX_ITEM_SCAN_DEPTH) return

  if (Array.isArray(node)) {
    for (const child of node) collectItemLabels(child, into, depth + 1)
    return
  }

  if (!isValidElement(node)) return

  const props = node.props as { value?: unknown; children?: ReactNode }

  if (node.type === SelectItem) {
    // Only a string value can be looked up by `SelectValue`, which reads the
    // context's `value: string`. A non-string here is a caller type error the
    // compiler already rejects; skipping it keeps this from minting a key
    // nothing can match.
    if (typeof props.value === 'string' && !into.has(props.value)) {
      into.set(props.value, props.children)
    }
    // An item's own children are its label, never another item.
    return
  }

  collectItemLabels(props.children, into, depth + 1)
}

function useSelectContext() {
  const context = useContext(SelectContext)
  if (!context) {
    throw new Error('Select components must be used within a Select provider')
  }
  return context
}

// ============================================================================
// Select Root
// ============================================================================

interface SelectProps {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  children: ReactNode
}

function Select({
  value: controlledValue,
  defaultValue = '',
  onValueChange,
  children,
}: SelectProps) {
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [open, setOpen] = useState(false)

  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : internalValue

  /*
    Open state is held HERE rather than left to Radix, even though Radix would
    manage it perfectly well: `SelectTrigger` has to withhold `aria-controls`
    while the panel is unmounted (#12865), and the closed/open discrimination
    is not readable from Radix's context by a consumer of the package.
  */
  const handleValueChange = useCallback(
    (radixValue: string) => {
      const next = fromRadixValue(radixValue)
      if (!isControlled) {
        setInternalValue(next)
      }
      onValueChange?.(next)
    },
    [isControlled, onValueChange],
  )

  const [triggerNaming, setTriggerNaming] =
    useState<SelectTriggerNaming | null>(null)
  /*
    Identity-stable when nothing changed. The trigger re-registers on every
    naming change, and returning the prior object keeps that from being a
    state write — and therefore a re-render — on every parent render. Every
    field is compared: omitting one makes that field's changes invisible to
    the panel, which is a stale name rather than a missing one.
  */
  const registerTriggerNaming = useCallback((naming: SelectTriggerNaming) => {
    setTriggerNaming(prior =>
      prior &&
      prior.id === naming.id &&
      prior.label === naming.label &&
      prior.labelledBy === naming.labelledBy
        ? prior
        : naming,
    )
  }, [])
  const listboxId = useId()
  const panelRef = useRef<HTMLDivElement | null>(null)

  /*
    Recomputed when `children` changes identity, which for a consumer holding
    its options in a constant is once. A consumer building them inline rebuilds
    the array every render and so rescans every render — the same cost that
    consumer already pays to recreate the elements, over a tree whose size is
    its own option count.
  */
  const itemLabels = useMemo(() => {
    const labels = new Map<string, ReactNode>()
    collectItemLabels(children, labels)
    return labels
  }, [children])

  return (
    <SelectContext
      value={{
        value,
        open,
        onOpenChange: setOpen,
        panelRef,
        triggerNaming,
        registerTriggerNaming,
        listboxId,
        itemLabels,
      }}
    >
      <SaiSelect
        value={toRadixValue(value)}
        onValueChange={handleValueChange}
        open={open}
        onOpenChange={setOpen}
      >
        {/* `relative` is kept because consumers lay out against it — the panel
            no longer needs it (Radix positions its own floating element), but
            removing a positioned block wrapper from 18 call sites is a layout
            change this slice does not make. */}
        <div className="relative">{children}</div>
      </SaiSelect>
    </SelectContext>
  )
}

// ============================================================================
// Select Trigger
// ============================================================================

interface SelectTriggerProps {
  id?: string
  className?: string
  children: ReactNode
  disabled?: boolean
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

function SelectTrigger({
  id,
  className,
  children,
  disabled = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: SelectTriggerProps) {
  const { open, registerTriggerNaming, listboxId } = useSelectContext()

  /*
    A rendered id, always. The caller's `id` still wins when it passes one — a
    visible `<label htmlFor>` depends on it — and the generated fallback exists
    so `SelectContent`'s `role="listbox"` can be NAMED by `aria-labelledby`
    whether the caller supplied an id or not. `[role="listbox"]` with no
    accessible name is axe's `aria-input-field-name`, serious, wcag2a.
  */
  const generatedId = useId()
  const domId = id ?? generatedId
  useEffect(() => {
    registerTriggerNaming({
      id: domId,
      label: ariaLabel,
      labelledBy: ariaLabelledBy,
    })
  }, [domId, ariaLabel, ariaLabelledBy, registerTriggerNaming])

  return (
    <SaiSelectTrigger
      id={domId}
      disabled={disabled}
      /*
        `listbox`, matching the role the panel renders. Radix advertises no
        `aria-haspopup` at all; the combobox pattern requires the popup's role
        to be declared on the control that opens it (#12796).
      */
      aria-haspopup="listbox"
      /*
        ONLY while open. Radix sets `aria-controls` unconditionally, and while
        the panel is unmounted that is a dangling IDREF —
        `aria-valid-attr-value`, a worse defect than the omission it papers
        over. The explicit `undefined` REMOVES Radix's attribute rather than
        merely failing to add one: sai-ui spreads these props onto
        `RadSelect.Trigger`, which spreads them after its own. Required on an
        expanded combobox: see `SelectContextValue.listboxId`.
      */
      aria-controls={open ? listboxId : undefined}
      aria-disabled={disabled || undefined}
      /*
        Hyphenated, and forwarded through sai-ui's `...rest` rather than as its
        camelCase `ariaLabel` / `ariaLabelledBy` props, because those are a
        discriminated union admitting only ONE of the two and #13186 turns on
        both being carried together. `transformAccessibilityProps` strips only
        the camelCase spellings.
      */
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      className={cn(
        // text-base = 16px floor (iOS Safari auto-zoom threshold); see --text-form-input in src/tokens/index.css and #7918 / WCAG 2.1 SC 1.4.4.
        'h-10 w-full rounded-lg border-stroke-light bg-surface-elevated px-3 py-2 text-base text-text-primary transition-colors',
        /*
          Structural utilities RE-ASSERTED rather than inherited. sai-ui's
          trigger base already carries all four, so tailwind-merge makes this
          a no-op today and the duplication costs nothing. It is here because
          the alternative is a silent one: a sai-ui bump that restyles its own
          trigger would drop the border width and the layout from every Select
          in the app, and nothing in the suite asserts a class this adapter
          does not own. Note `border-stroke-light` above is a COLOUR — on
          its own it paints a border that has no width.
        */
        'flex items-center justify-between border',
        'hover:bg-surface-panel',
        'focus:outline-none focus:ring-2 focus:ring-stroke-selected',
        'disabled:cursor-not-allowed disabled:opacity-50',
        // The chevron is sai-ui's (`RadSelect.Icon`), so the open-state flip
        // the bespoke trigger animated is expressed against Radix's own
        // `data-state` rather than against React state.
        '[&>svg]:opacity-50 [&>svg]:transition-transform',
        'data-[state=open]:[&>svg]:rotate-180',
        className,
      )}
    >
      {children}
    </SaiSelectTrigger>
  )
}

// ============================================================================
// Select Value
// ============================================================================

interface SelectValueProps {
  placeholder?: string
}

/**
 * Renders the RAW value, not the selected item's text — deliberately NOT
 * `RadSelect.Value`.
 *
 * Two call sites carry standing comments explaining that they render their own
 * label in the trigger precisely because this component shows the raw value
 * (`src/app/welcome/operator-panel.tsx:867` and `:966` — "would show
 * 'athletic-director' instead of 'Athletic Director'"). Swapping in Radix's
 * `Value` would silently change what all 8 `<SelectValue />` call sites
 * display, which is a consumer-visible change and not this slice's to make.
 */
function SelectValue({ placeholder }: SelectValueProps) {
  const { value, itemLabels } = useSelectContext()

  /*
    `has`, not `??` on the lookup. An item may legitimately render NOTHING as
    its label — `<SelectItem value="none" />` and an item whose children are
    `null` both do — and a nullish-coalescing fallback would print that item's
    raw value instead of the blank its author chose. Distinguishing "no such
    item" from "this item displays as nothing" is the whole reason the map is
    keyed rather than defaulted.
  */
  const label = itemLabels.has(value) ? itemLabels.get(value) : value

  /*
    `!value` still drives the muted colour, NOT `!label`. The muted treatment
    means "nothing is chosen yet", which is a fact about the value; an item
    that displays as empty has been chosen, and colouring its blank trigger as
    a placeholder would say otherwise.
  */
  return (
    <span className={cn(!value && 'text-text-secondary')}>
      {value ? label : placeholder}
    </span>
  )
}

// ============================================================================
// Select Content
// ============================================================================

interface SelectContentProps {
  children: ReactNode
  className?: string
  /**
   * Historically: render the panel into `document.body` so an ancestor's
   * `overflow` could not clip it. Radix portals in BOTH modes, so the clipping
   * problem is gone either way and this prop now selects only the two things
   * that stayed observable — the `data-select-portal` marker, and closing on
   * an ancestor scroll (a portalled panel goes stale when the page under it
   * moves; an in-flow one never did this and still does not).
   */
  portal?: boolean
}

function SelectContent({
  children,
  className,
  portal = false,
}: SelectContentProps) {
  const { open, onOpenChange, panelRef, triggerNaming, listboxId } =
    useSelectContext()

  // Close on scroll/resize while open in portal mode (no reflow tracking in
  // v1). Capture-phase scroll catches nested scrolls in ancestor containers so
  // the panel doesn't float away from the trigger — but we have to exempt
  // scrolls originating inside the panel itself, otherwise wheeling the
  // dropdown's own option list closes the panel mid-scroll.
  useEffect(() => {
    if (!portal || !open) return
    const onScroll = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return
      onOpenChange(false)
    }
    const onResize = () => onOpenChange(false)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [portal, open, onOpenChange, panelRef])

  /*
    How the `role="listbox"` panel below is NAMED. See {@link
    SelectTriggerNaming} for the accname analysis, the measurement, and — the
    part not to skip — why the third case leaves the panel unnamed in Chromium
    rather than naming it (#13186).

    The rule is about WHERE each attribute came from, not about emitting only
    one. Both of the CALLER's attributes are copied verbatim whenever it passed
    them, because the panel then resolves them by the same accname steps in the
    same order as the trigger — including the fallback the trigger relies on.
    Suppressing `aria-label` "because `aria-labelledby` wins" breaks exactly
    that case: accname step 2B applies only when `aria-labelledby` contains at
    least one VALID IDREF, so a dangling one is skipped and step 2C's
    `aria-label` names the trigger. Dropping the label would leave the trigger
    named and the panel unnamed — a new instance of this very bug, introduced
    by over-applying its fix.

    What must never be paired with `aria-label` is the SYNTHESIZED `{id}`
    fallback, and that asymmetry is the whole point: `{id}` is guaranteed to
    resolve, because the trigger that rendered it is in the DOM. So it always
    wins step 2B, always recurses into the combobox, and always computes empty
    — it would silently beat a perfectly good `aria-label` every time rather
    than only when the caller's own reference broke.
  */
  const callerLabelledBy = triggerNaming?.labelledBy
  const callerLabel = triggerNaming?.label
  const hasCallerNaming = Boolean(callerLabelledBy ?? callerLabel)

  const panelLabel = hasCallerNaming ? callerLabel : undefined
  const panelLabelledBy = hasCallerNaming ? callerLabelledBy : triggerNaming?.id

  /*
    One consequence for tests, since it is not obvious from the markup: while
    the panel is open the combobox and the listbox carry the SAME `aria-label`,
    and Testing Library's `getByLabelText` matches `[aria-label]` on any
    element. So a `getByLabelText(<the trigger's label>)` executed with the
    panel open throws "found multiple elements" — scope it to the trigger with
    `getByRole('combobox', { name })` instead, BEFORE opening the panel (Radix
    aria-hides the trigger while the panel is open).
  */

  return (
    <SaiSelectContent
      ref={panelRef}
      /*
        Overrides the content id Radix generates, so `SelectTrigger`'s
        `aria-controls` — minted in the root, where the trigger can read it
        while this panel is unmounted — resolves to the element that carries
        `role="listbox"`.
      */
      id={listboxId}
      aria-label={panelLabel}
      aria-labelledby={panelLabelledBy}
      data-select-portal={portal ? '' : undefined}
      /*
        The scroll affordances have to move with the panel fill, or they are
        invisible. Radix mounts these two buttons only when the list actually
        overflows, and sai-ui paints them for ITS menu surface: a
        `from-surface-menu via-surface-menu` gradient with a literal
        `text-white` chevron. That pairing is correct in sai-ui, where
        `surface-menu` is a dark fill — but the panel above deliberately
        replaces that fill with the light `bg-surface-elevated` (#16837), so
        the dark gradient would sit as a dark band on a white panel. The
        affordances follow the panel fill instead.

        Invisible to every gate we have: jsdom applies no CSS, and the unit
        pins read class strings on the panel rather than on these children.
        So the classes are asserted in `select-sai-adapter-13213.test.tsx`
        the same way `z-menu` is, for the same reason.
      */
      scrollUpButtonClassName="from-surface-elevated via-surface-elevated text-text-secondary"
      scrollDownButtonClassName="from-surface-elevated via-surface-elevated text-text-secondary"
      className={cn(
        /*
          sai-ui's menu layer, restated so the pin in
          `select-sai-adapter-13213.test.tsx` keeps the panel above every
          modal and drawer (`z-modal`) and under the feedback band. Until
          #16990 sai-ui's own `z-menu` emitted no CSS here (the package's
          `index.css` was not loaded), the panel computed `z-index: auto` and
          this line was an explicit `z-[1000]`.
        */
        'z-menu',
        // Cadence surface tokens over sai-ui's dark menu surface: this panel
        // sits on the app's own light/dark surfaces at all 18 call sites, and
        // `bg-surface-menu` / `text-white-static` would invert it. Elevated,
        // not the page surface: a listbox is a card above the page (#16837).
        'max-h-60 border-stroke-regular bg-surface-elevated py-1 text-text-primary',
        /*
          Re-asserted for the same reason as the trigger's `border` above:
          both come from sai-ui's content base today, and nothing would notice
          if a bump removed them. `rounded-lg` also pins the radius back to
          the bespoke component's value, since sai-ui's base is `rounded-xl`.

          `overflow-auto` is deliberately NOT re-asserted, even though the
          bespoke panel carried it. Under Radix the scrolling belongs to the
          inner Viewport and the content stays `overflow-hidden`; forcing a
          second scroll container here would break the scroll buttons the
          `scrollUpButtonClassName` override above exists to make visible.
        */
        'rounded-lg border shadow-lg',
        className,
      )}
    >
      {children}
    </SaiSelectContent>
  )
}

// ============================================================================
// Select Item
// ============================================================================

interface SelectItemProps {
  value: string
  children: ReactNode
  className?: string
  /**
   * Renders the option, but never lets it be chosen — by pointer or by
   * keyboard.
   *
   * Added by #12792 for a "coming soon" option that has to stay VISIBLE: the
   * ActionBoard demo rail lists SchoolAI data as a seed the product is
   * committed to and cannot yet deliver, and dropping it from the list would
   * remove exactly the thing the option is there to say. `SelectTrigger`
   * already had this prop; the item did not, so the state was unexpressible
   * below the trigger.
   *
   * Radix renders it as `aria-disabled` plus `data-disabled` on the
   * `role="option"` div — never the `disabled` attribute, which is not valid
   * on one — and refuses selection by pointer and by key.
   */
  disabled?: boolean
}

function SelectItem({
  value,
  children,
  className,
  disabled = false,
}: SelectItemProps) {
  return (
    <SaiSelectItem
      // See EMPTY_VALUE_SENTINEL: Radix throws on an item whose value is `''`,
      // and three live call sites pass one.
      value={toRadixValue(value)}
      disabled={disabled}
      /*
        Out of the tab order when disabled, focusable otherwise. A focus stop
        that cannot be actioned is worse than no stop: it reads as a control to
        a keyboard user and then does nothing. Radix's composite would make
        every option `-1`; #12792 pins Cadence's options as reachable.
      */
      tabIndex={disabled ? -1 : 0}
      className={cn(
        // text-base = 16px floor (iOS Safari auto-zoom threshold); see --text-form-input in src/tokens/index.css and #7918 / WCAG 2.1 SC 1.4.4.
        'py-2 text-base text-text-primary',
        disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-fill-10',
        'focus:bg-fill-10 data-[state=checked]:bg-fill-10',
        className,
      )}
    >
      {children}
    </SaiSelectItem>
  )
}

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem }
